import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from "@mui/material"
import {useTheme} from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import {Box} from "@mui/system"
import {useState} from "react"
import {ClimbigEventType, ClimbingEvent} from "../../../models/ClimbingEvent"
import {createNewEvent, updateEvent} from "../../../services/events/EventService"
import {AddingGroups} from "../groups/AddingGroups"
import {DateRangePicker} from "./DateRangePicker"
import {MathTextField} from "./MathTextField";
import {AddingAgeGroups} from "../groups/AddingAgeGroups";


type Props = {
  handleSaveNewEvent: (name: string) => void,
  updateMode: boolean,
  event?: ClimbingEvent
}
export const CreateNewEvent: React.FC<Props> = ({handleSaveNewEvent, updateMode, event}) => {

  const [climbingEvent, setClimbingEvent] = useState(event || new ClimbingEvent("", Date.now(), Date.now(), []))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string>()
  const [calcError, setCalcError] = useState<string>()
  const isNameToShort = () => {
    return climbingEvent.name.length < 3
  }
  const largeScreen = useMediaQuery(useTheme().breakpoints.up('md'));
  const saveIfValuesAreNotEmpty = () => {
    setError(undefined)
    if (!isNameToShort() && climbingEvent.startDate && climbingEvent.endDate) {
      setSaving(true)
      if (updateMode) {
        updateEvent(climbingEvent).then(() => {
          setSaving(false)
          handleSaveNewEvent(climbingEvent.name)
        }).catch(error => {
          setError(error.message)
        })
      } else {
        createNewEvent(climbingEvent).then(() => {
          setSaving(false)
          handleSaveNewEvent(climbingEvent.name)
        }).catch(error => {
          setError(error.message)
        });
      }
    } else {
      setError("Nazwa musi mieć conajmniej 3 znaki a daty muszą być wybrane")
    }
  }

  return (
    <Box component="form" sx={{display: "flex", alignItems: "center", flexDirection: "column",}}>
      <Grid container spacing={2} justifyContent="center" alignItems="center" direction={largeScreen ? "row" : "column"}
            sx={{
              '& .MuiTextField-root': {m: 2, width: 300},
            }}>
        {!updateMode &&
            <Grid item xs={6} md={6} justifyContent="center" display="flex">
                <TextField
                    error={isNameToShort()}
                    label="Nazwa"
                    id="event-name"
                    value={climbingEvent.name}
                    helperText={isNameToShort() ? "Nazwa musi mieć conajmniej 3 znaki" : ""}
                    onChange={(e) => setClimbingEvent({...climbingEvent, name: e.target.value})}
                />
            </Grid>
        }

        <Grid item xs={12} md={12} justifyContent="center" display="flex">
          <DateRangePicker
            startDateValue={climbingEvent.startDate}
            startDateChange={(startDate) => setClimbingEvent({...climbingEvent, startDate})}
            endDateValue={climbingEvent.endDate}
            endDateChange={(endDate) => setClimbingEvent({...climbingEvent, endDate})}
            timePicker={false}
          />
        </Grid>

        {climbingEvent.type !== ClimbigEventType.BOULDER &&
            <>

                <Grid item xs={12} md={12} justifyContent="center" display="flex">
                    <Paper elevation={3} sx={{p: 2, margin: 2, width: "100%"}}>
                        <Typography variant="h6" component="h2">Grupy</Typography>
                    <AddingGroups groups={climbingEvent.groups}
                                  handleUpdateGroup={(groups) => setClimbingEvent({...climbingEvent, groups})}/>
                    </Paper>
                </Grid>
                <Grid item xs={12} md={12} justifyContent="center" display="flex">
                    <Paper elevation={3} sx={{p: 2, margin: 2, width: "100%"}}>
                        <Typography variant="h6" component="h2">Grupy Wiekowe</Typography>
                    <AddingAgeGroups ageGroups={climbingEvent.ageGroups? climbingEvent.ageGroups : []}
                                     handleUpdateAgeGroup={(ageGroups) => setClimbingEvent({
                                       ...climbingEvent,
                                       ageGroups
                                     })}/>
                    </Paper>
                </Grid>
            </>
        }

        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <TextField
            label="Link do informacji"
            id="event-info-link"
            type={"url"}
            value={climbingEvent.moreInfoLink}
            onChange={(e) => setClimbingEvent({...climbingEvent, moreInfoLink: e.target.value})}
          />
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <TextField
            label="Link do wyników"
            id="event-results-link"
            type={"url"}
            value={climbingEvent.resultsLink}
            onChange={(e) => setClimbingEvent({...climbingEvent, resultsLink: e.target.value})}
          />
        </Grid>
      </Grid>
      <Typography variant={"h5"} mb={1}>Punktacja</Typography>
      <Grid container spacing={2} justifyContent="center" alignItems="center" direction={largeScreen ? "row" : "column"}
            sx={{
              '& .MuiTextField-root': {m: 2, width: 300},
            }}>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <MathTextField
            label="Nic"
            value={climbingEvent.nothingPoints}
            onChange={(value) => setClimbingEvent({...climbingEvent, nothingPoints: value})}
            onErrorChange={(error) => setCalcError(error)}
            required={true}
          />
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <MathTextField
            label="Zona"
            value={climbingEvent.zonePoints || ""}
            onChange={(value) => setClimbingEvent({...climbingEvent, zonePoints: value})}
            onErrorChange={(error) => setCalcError(error)}
            required={false}
          />
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <MathTextField
            label="Zona 2"
            value={climbingEvent.zone2Points || ""}
            onChange={(value) => setClimbingEvent({...climbingEvent, zone2Points: value})}
            onErrorChange={(error) => setCalcError(error)}
            required={false}
          />
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <MathTextField
            label="TOP"
            value={climbingEvent.donePoints || ""}
            onChange={(value) => setClimbingEvent({...climbingEvent, donePoints: value})}
            onErrorChange={(error) => setCalcError(error)}
            required={true}
          />
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <MathTextField
            label="Flash"
            value={climbingEvent.flashPoints || ""}
            onChange={(value) => setClimbingEvent({...climbingEvent, flashPoints: value})}
            onErrorChange={(error) => setCalcError(error)}
            required={false}
          />
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <TextField
            label="Maksymalna liczba dróg"
            id="event-max-roads"
            type={"number"}
            value={climbingEvent.limit}
            onChange={(e) => setClimbingEvent({...climbingEvent, limit: parseInt(e.target.value) || undefined})}
          />
        </Grid>
      </Grid>
      <Typography variant={"h5"} mb={1}>Ustawienia dodatkowe</Typography>
      <Grid container spacing={2} justifyContent="center" alignItems="center" direction={largeScreen ? "row" : "column"}
            sx={{
              '& .MuiTextField-root': {m: 2, width: 300},
            }}>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <FormControlLabel labelPlacement="start" control={
            <Checkbox
              checked={climbingEvent.separateZonePoint}
              onChange={(e) => setClimbingEvent({...climbingEvent, separateZonePoint: e.target.checked, separateFlashPoint: false})}
            />
          } label="Osobna punktacja zon"/>
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <FormControlLabel labelPlacement="start" control={
            <Checkbox
                checked={climbingEvent.separateFlashPoint}
                onChange={(e) => setClimbingEvent({...climbingEvent, separateZonePoint: false, separateFlashPoint: e.target.checked})}
            />
          } label="Osobna punktacja Flashy"/>
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <FormControlLabel labelPlacement="start" control={
            <Checkbox
              checked={climbingEvent.showGrade}
              onChange={(e) => setClimbingEvent({...climbingEvent, showGrade: e.target.checked})}
            />
          } label="trudnosci baldow"/>
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <FormControlLabel labelPlacement="start" control={
            <Checkbox
              checked={climbingEvent.showRoutesForUsers}
              onChange={(e) => setClimbingEvent({...climbingEvent, showRoutesForUsers: e.target.checked})}
            />
          } label="pokazuj drogi w wynikach"/>
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <FormControlLabel labelPlacement="start" control={
            <Checkbox
              checked={climbingEvent.allowSection}
              onChange={(e) => setClimbingEvent({...climbingEvent, allowSection: e.target.checked})}
            />
          } label="Osobna klasyfikacja dla sekcji"/>
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <FormControlLabel labelPlacement="start" control={
            <Checkbox
              checked={climbingEvent.collectCompetitionData}
              onChange={(e) => setClimbingEvent({...climbingEvent, collectCompetitionData: e.target.checked})}
            />
          } label="Zbieraj dokladne dane od użytkowników"/>
        </Grid>
        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <FormControlLabel labelPlacement="start" control={
            <Checkbox
              checked={climbingEvent.collectUserId}
              onChange={(e) => setClimbingEvent({...climbingEvent, collectUserId: e.target.checked})}
            />
          } label="Zbieraj numer startowy użytkownika"/>
        </Grid>

        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <FormControlLabel labelPlacement="start" control={
            <Checkbox
              checked={climbingEvent.liveResults}
              onChange={(e) => setClimbingEvent({...climbingEvent, liveResults: e.target.checked})}
            />
          } label="Wyniki na żywo"/>
        </Grid>

        <Grid item xs={8} md={4} justifyContent="center" display="flex">
          <FormControlLabel labelPlacement="start" control={
            <Checkbox
              checked={climbingEvent.useStartAndEndDate}
              onChange={(e) => setClimbingEvent({...climbingEvent, useStartAndEndDate: e.target.checked})}
            />
          } label="Użyj dat wydarzenia aby je zablokowac"/>
        </Grid>


      </Grid>
      <Button variant="contained" disabled={saving || !!calcError} sx={{p: 2, pr: 4, pl: 4, m: 2}}
              onClick={() => saveIfValuesAreNotEmpty()}>Zapisz</Button>
      <Typography color="error" sx={{display: "block", mt: 2}}>{error || ""}</Typography>
      <Typography>{saving ? "Zapisywanie..." : ""}</Typography>

    </Box>
  )
}